import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface PressRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  media_outlet: string;
  position: string;
  media_type: string;
  other_media_type?: string;
  team_size: string;
  equipment_details?: string;
  coverage_plans: string;
  arrival_date: string;
  special_requests?: string;
  registration_number: string;
  status: string;
  created_at: string;
  updated_at: string;
  tournament_id?: string;
  tournament_name?: string;
}

interface PressRegistrationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  registration: PressRegistration;
  onStatusChange?: (status: string) => void;
  onDelete?: () => void;
}

export default function PressRegistrationDetailsModal({
  isOpen,
  onClose,
  registration,
  onStatusChange,
  onDelete
}: PressRegistrationDetailsModalProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <div className="bg-golf-green px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <Dialog.Title className="text-xl font-display font-bold text-white">
                      Press Registration Details
                    </Dialog.Title>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={onClose}
                        className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <div className="mt-2">
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                            <div>
                              <h3 className="text-lg font-bold text-golf-gray">
                                {registration.first_name} {registration.last_name}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {registration.media_outlet} - {registration.position}
                              </p>
                            </div>
                            <div>
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                registration.status === 'approved' ? 'bg-green-100 text-green-800' :
                                registration.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-3 sm:mt-4 text-sm text-gray-500">
                            <p>Registration #: <span className="font-medium text-gray-700">{registration.registration_number}</span></p>
                            <p>Registered on: <span className="font-medium text-gray-700">{formatDate(registration.created_at)}</span></p>
                            {registration.tournament_name && (
                              <p>Tournament: <span className="font-medium text-gray-700">{registration.tournament_name}</span></p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <h4 className="text-md font-semibold text-golf-gray mb-4">Contact Information</h4>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium text-golf-gray">{registration.email}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Phone</p>
                                <p className="font-medium text-golf-gray">{registration.phone}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-md font-semibold text-golf-gray mb-4">Media Details</h4>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm text-gray-500">Media Type</p>
                                <p className="font-medium text-golf-gray">
                                  {(() => {
                                    let types: string[] = [];
                                    try {
                                      const parsed = JSON.parse(registration.media_type);
                                      types = Array.isArray(parsed) ? parsed : [registration.media_type];
                                    } catch {
                                      types = [registration.media_type];
                                    }
                                    return types
                                      .map(t => t === 'Other' && registration.other_media_type ? registration.other_media_type : t)
                                      .join(', ');
                                  })()}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Team Size</p>
                                <p className="font-medium text-golf-gray">{registration.team_size}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 sm:mt-6">
                          <h4 className="text-md font-semibold text-golf-gray mb-4">Coverage Details</h4>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-gray-500">Coverage Plans</p>
                              <p className="font-medium text-golf-gray whitespace-pre-line">{registration.coverage_plans}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Arrival Date</p>
                              <p className="font-medium text-golf-gray">{formatDate(registration.arrival_date)}</p>
                            </div>
                            {registration.equipment_details && (
                              <div>
                                <p className="text-sm text-gray-500">Equipment Details</p>
                                <p className="font-medium text-golf-gray whitespace-pre-line">{registration.equipment_details}</p>
                              </div>
                            )}
                            {registration.special_requests && (
                              <div>
                                <p className="text-sm text-gray-500">Special Requests</p>
                                <p className="font-medium text-golf-gray whitespace-pre-line">{registration.special_requests}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <div className="flex flex-col sm:flex-row w-full sm:justify-end gap-2 sm:gap-0">
                    {registration.status === 'pending' && onStatusChange && (
                      <>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => onStatusChange('approved')}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => onStatusChange('rejected')}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={onClose}
                    >
                      Close
                    </button>
                    {onDelete && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-red-300 bg-white px-4 py-2 text-base font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={onDelete}
                      >
                        <TrashIcon className="w-5 h-5 mr-2" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
